import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import {
  buildBreadcrumbJsonLd,
  buildCanonicalUrl,
  buildWebPageJsonLd,
  organizationJsonLd,
  websiteJsonLd,
} from "@/lib/seo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { TeamMember } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { Linkedin, Twitter, Mail, Users } from "lucide-react";

interface TeamMemberAPI {
  id: number;
  name: string;
  position: string;
  experience: string;
  image: string;
  imageUrl?: string;
  linkedinUrl: string;
  twitterUrl: string;
  email: string;
  specialization?: string;
  bio?: string;
}

export default function TeamPage() {
  const teamMembers = [
    {
      id: 1,
      name: "Sushma Upadhyay",
      position: "HR Manager",
      bio: "Expert in human resources and administrative operations with extensive experience in team management.",
      image: "/team/sushma-upadhyay.png",
      experience: "8 years",
      specialization: "Human Resources",
      email: "hr@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 2,
      name: "Barkha Kumari",
      position: "Manager",
      bio: "Customer support specialist with comprehensive experience in travel operations management.",
      image: "/team/barkha-kumari-actual.png",
      experience: "5 years",
      specialization: "Customer Support",
      email: "support@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 3,
      name: "Ankit Sharma",
      position: "Operation Head",
      bio: "Seasoned operations leader with comprehensive experience in travel industry management.",
      image: "/team/ankit-sharma.png",
      experience: "18 years",
      specialization: "Operations Management",
      email: "ankit@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 4,
      name: "Anil Bharti",
      position: "Branch Head Vasant Kunj",
      bio: "Branch management expert with extensive experience in strategic planning and operations.",
      image: "/team/anil-bharti.png",
      experience: "25 years",
      specialization: "Branch Management",
      email: "anil@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 5,
      name: "Veer Bahadur Singh",
      position: "Passport & Visa Specialist",
      bio: "Specialist in passport and visa services with extensive knowledge of international documentation.",
      image: "/team/veer-bahadur-singh.png",
      experience: "16 years",
      specialization: "Documentation Services",
      email: "veer@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 6,
      name: "Meeta Arora",
      position: "DGM",
      bio: "Deputy General Manager with expertise in strategic operations and business development.",
      image: "/team/meeta-arora.png",
      experience: "16 years",
      specialization: "General Management",
      email: "meeta@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 7,
      name: "Rakhi Verma",
      position: "DGM- Outbound Tour",
      bio: "Deputy General Manager specializing in outbound tour operations and international travel planning.",
      image: "/team/rakhi-verma.png",
      experience: "15 years",
      specialization: "Outbound Tours",
      email: "rakhi@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 8,
      name: "Gaurav Sehgal",
      position: "Operation Head-Ticketing",
      bio: "Operations specialist with extensive experience in ticketing and customer service.",
      image: "/team/gaurav-sehgal.png",
      experience: "25 years",
      specialization: "Ticketing Operations",
      email: "gaurav@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 9,
      name: "Syed Ajaz Ahmad",
      position: "Operation Head-Ticketing",
      bio: "Operations specialist with extensive experience in ticketing and customer service.",
      image: "/team/barkha-kumari.png",
      experience: "24 years",
      specialization: "Ticketing Operations",
      email: "ahmad@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 10,
      name: "Umesh Chandra Joshi",
      position: "Operation Head",
      bio: "Operations management expert with comprehensive experience in travel industry operations.",
      image: "/team/umesh-chandra-joshi.png",
      experience: "18 years",
      specialization: "Operation Manager: Ticketing",
      email: "umesh@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 11,
      name: "Pushpendra Sharma",
      position: "Accounts Head",
      bio: "Financial management expert with extensive experience in accounting and financial operations.",
      image: "/team/member6.png",
      experience: "16 years",
      specialization: "Financial Management",
      email: "pushpendra.sharma@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 12,
      name: "Dhairju Mishra",
      position: "Accounts Head",
      bio: "Financial management expert with focus on accounting operations and financial planning.",
      image: "/team/dhairju-mishra.png",
      experience: "21 years",
      specialization: "Financial Management",
      email: "dhairju@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
    {
      id: 13,
      name: "Neesant Chaurasiya",
      position: "Accounts Manager",
      bio: "Account management specialist with expertise in financial operations and client management.",
      image: "/team/neesant-kumar-chaurasiya.png",
      experience: "14 years",
      specialization: "Account Management",
      email: "nkumar@travelwingsonline.in",
      linkedinUrl: "#",
      twitterUrl: "#",
    },
  ];

  const isLoading = false;

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Leadership & Team | Travel Wings Global"
        description="Meet the Travel Wings Global leadership and operations team delivering forex and travel excellence."
        path="/team"
        structuredData={[
          organizationJsonLd,
          websiteJsonLd,
          buildWebPageJsonLd({
            name: "Leadership & Team | Travel Wings Global",
            description:
              "Travel Wings Global leadership and team across forex, travel, and customer success.",
            url: buildCanonicalUrl("/team"),
            type: "AboutPage",
          }),
          buildBreadcrumbJsonLd([
            { name: "Home", url: buildCanonicalUrl("/") },
            { name: "Team", url: buildCanonicalUrl("/team") },
          ]),
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Our <span className="text-purple-600">Team</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Meet the dedicated professionals who make your travel dreams and
            financial goals come true
          </p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-950 rounded-full text-sm font-medium mb-4">
              <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
              Meet Our Experts
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-purple-600">Professional Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our experienced team brings decades of combined expertise in
              travel and financial services
            </p>
          </div>

          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="text-center group animate-pulse">
                  <div className="w-48 h-48 bg-gray-300 rounded-3xl mx-auto mb-8"></div>
                  <div className="h-6 bg-gray-300 rounded mb-2 mx-auto w-32"></div>
                  <div className="h-4 bg-gray-300 rounded mb-3 mx-auto w-24"></div>
                  <div className="h-4 bg-gray-300 rounded mb-4 mx-auto w-28"></div>
                  <div className="flex justify-center space-x-3">
                    <div className="w-6 h-6 bg-gray-300 rounded"></div>
                    <div className="w-6 h-6 bg-gray-300 rounded"></div>
                    <div className="w-6 h-6 bg-gray-300 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {teamMembers?.map((member) => (
                <div key={member.id} className="text-center group">
                  <div className="relative mb-8">
                    <div className="relative w-48 h-48 mx-auto">
                      <img
                        src={`${member.image || member.imageUrl}?cache=${Date.now()}`}
                        alt={member.name}
                        width={192}
                        height={192}
                        className="w-full h-full rounded-3xl object-cover shadow-2xl group-hover:shadow-3xl transition-all duration-300"
                        loading="eager"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-blue-600/20 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                    {member.name}
                  </h3>
                  <p className="text-blue-600 font-semibold mb-3 text-lg text-center">
                    {member.position}
                  </p>
                  <p className="text-gray-600 mb-6 max-w-xs mx-auto text-center">
                    {member.experience}
                  </p>
                  <div className="flex justify-center space-x-3">
                    {member.email && (
                      <a
                        href={`mailto:${member.email}`}
                        className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 hover:bg-orange-200 transition-colors group-hover:scale-110 transform duration-300"
                        title={`Email ${member.name}`}
                        aria-label={`Email ${member.name}`}
                      >
                        <Mail className="w-6 h-6" aria-hidden />
                      </a>
                    )}
                    <div className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
                      {member.specialization}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
